import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Diagnose Service Failures',
    description: 'Systematically identify all services that are failing to start and understand why they are failing.',
    requirements: [
      {
        text: 'Identify all failed services using system management tools'
      },
      {
        text: 'Review logs for each failed service to understand failure reasons'
      },
      {
        text: 'Check service configuration files for syntax errors'
      },
      {
        text: 'Verify service dependencies and their current states'
      },
      {
        text: 'Document all issues found for systematic resolution'
      }
    ],
    verification: [
      'You have identified all failed services',
      'You understand why each service is failing',
      'You have checked all relevant configuration files',
      'You have identified missing dependencies',
      'You have a clear list of issues to fix'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Fix Service Configuration',
    description: 'Correct syntax errors and misconfigurations in systemd service files that are preventing services from starting.',
    requirements: [
      {
        text: 'Locate and examine systemd service files for the following services:',
        subitems: ['webapp.service', 'backend-api.service', 'monitoring.service']
      },
      {
        text: 'Fix syntax errors in service unit files'
      },
      {
        text: 'Correct invalid directives and options'
      },
      {
        text: 'Ensure ExecStart paths point to valid executables'
      },
      {
        text: 'Reload systemd daemon after making changes'
      }
    ],
    verification: [
      'All service files have valid systemd syntax',
      'ExecStart paths are correct and executable',
      'Service types are properly configured',
      'Working directories exist and are accessible',
      'Systemd daemon has been reloaded'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Resolve Dependencies',
    description: 'Ensure all service dependencies are properly defined and that dependent services start in the correct order.',
    requirements: [
      {
        text: 'Identify service dependency chains using systemd tools'
      },
      {
        text: 'Add missing After= and Requires= directives'
      },
      {
        text: 'Ensure database service starts before application services'
      },
      {
        text: 'Configure proper network dependencies for network services'
      },
      {
        text: 'Verify dependency resolution works correctly'
      }
    ],
    verification: [
      'Service dependencies are properly defined',
      'Database starts before dependent services',
      'Network is available before network services start',
      'Dependency chains resolve without circular dependencies',
      'Services start in the correct order'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Fix Permissions and Users',
    description: 'Create missing users and groups, and fix file permissions that are preventing services from starting.',
    requirements: [
      {
        text: 'Create missing service users and groups:',
        subitems: ['webapp user and group', 'apiuser user and group', 'monitor user and group']
      },
      {
        text: 'Set correct ownership on service executables and directories'
      },
      {
        text: 'Fix execute permissions on service binaries'
      },
      {
        text: 'Ensure log directories exist with proper permissions'
      },
      {
        text: 'Verify PID file directories are writable by service users'
      }
    ],
    verification: [
      'All required users and groups exist',
      'Service executables have correct ownership',
      'Binaries have execute permissions',
      'Log directories are writable by service users',
      'PID file locations are accessible'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Configure Environment Variables',
    description: 'Set up required environment variables and configuration files that services need to start properly.',
    requirements: [
      {
        text: 'Create environment files referenced in service units (required path: /etc/backend/env)'
      },
      {
        text: 'Set required environment variables:',
        subitems: [
          'API_PORT=3000',
          'NODE_ENV=production',
          'DATABASE_URL=postgresql://localhost/backend'
        ]
      },
      {
        text: 'Ensure configuration files exist in expected locations'
      },
      {
        text: 'Set proper permissions on sensitive configuration files'
      },
      {
        text: 'Validate environment variable syntax'
      }
    ],
    verification: [
      'All environment files exist and are readable',
      'Required environment variables are defined',
      'Configuration files are in correct locations',
      'Sensitive files have restricted permissions',
      'Services can read their configuration'
    ]
  },
  {
    id: 'task-6',
    number: 6,
    title: 'Resolve Port Conflicts',
    description: 'Identify and resolve port conflicts between services that are preventing them from binding to required ports.',
    requirements: [
      {
        text: 'Identify which services are trying to use the same ports'
      },
      {
        text: 'Check current port usage with system tools'
      },
      {
        text: 'Modify service configurations to use different ports:',
        subitems: [
          'webapp on port 8080',
          'backend-api on port 3000',
          'monitoring service environment set to MONITOR_PORT=9090'
        ]
      },
      {
        text: 'Update any hardcoded port references'
      },
      {
        text: 'Ensure firewall rules allow the new ports if needed'
      }
    ],
    verification: [
      'No port conflicts exist between services',
      'webapp and backend-api bind to their designated ports',
      'monitoring.service has MONITOR_PORT=9090 configured',
      'Port configurations are consistent',
      'Network connectivity works as expected'
    ]
  },
  {
    id: 'task-7',
    number: 7,
    title: 'Start and Enable Services',
    description: 'Start all fixed services and ensure they are enabled to start automatically at boot.',
    requirements: [
      {
        text: 'Start each service individually and verify it starts successfully'
      },
      {
        text: 'Enable services for automatic startup at boot'
      },
      {
        text: 'Verify all services are running without errors'
      },
      {
        text: 'Test service functionality to ensure they work correctly'
      },
      {
        text: 'Check that services restart properly after a reboot simulation'
      }
    ],
    verification: [
      'All services are in active (running) state',
      'Services are enabled for automatic startup',
      'No error messages in service logs',
      'Services respond to their respective requests',
      'Services maintain running state over time'
    ]
  }
];

export function ServiceStartupFailuresInstructions() {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setCurrentTaskIndex(0);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);
    
    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }
    
    setCompletedChecks(newChecks);

    // Check if all verifications for this task are completed
    const currentTask = tasks[currentTaskIndex];
    const allChecksCompleted = currentTask.verification.every(
      (_, idx) => newChecks.has(`${currentTask.id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>
            
            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>
            
            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Footer */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>
        
        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="flex gap-[10px]">
          <button
            onClick={handlePreviousTask}
            disabled={currentTaskIndex === 0}
            className="px-4 py-2 bg-[#2d3748] border border-[#374151] text-[#cbd5e1] rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#374151] hover:text-[#e2e8f0] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNextTask}
            className="px-5 py-2 bg-gradient-to-r from-[#10b981] to-[#059669] text-[#1a1f2e] rounded-md text-[13px] font-semibold cursor-pointer transition-all hover:shadow-lg hover:scale-105"
          >
            Next Task
          </button>
        </div>
      </div>
    </div>
  );
}

export default ServiceStartupFailuresInstructions;
